import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Checkbox, FormControlLabel, Button, Typography,
  TextField, Stack, Tooltip, Avatar, Paper, IconButton,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import axios from 'axios';
import { Items } from '../../src/modules/Menu/Items';
import config from '../config/config';
import SnackbarAlert from '../components/SnackbarAlert';
import SimpleSuccessSpinner from '../components/LoadingSpinner';

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.85rem',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(6,85,165,0.08)' },
  },
  '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.85rem' },
};

const ModalPermisos = ({ open, onClose, userId, userName = '', onSave }) => {
  const [permissions, setPermissions] = useState([]);        // IDs seleccionados
  const [selectAll, setSelectAll] = useState(false);         // toggle global
  const [query, setQuery] = useState('');                    // búsqueda
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [loading, setLoading] = useState(false);             // guardar
  const [fetching, setFetching] = useState(false);           // cargar permisos al abrir

  const loggedInUsername = sessionStorage.getItem('loggedInUsername');

  // Cargar permisos al abrir
  useEffect(() => {
    if (!open || !userId) return;
    (async () => {
      setFetching(true);
      try {
        const res = await axios.get(`${config.apiBaseUrl}/Permisos2/${userId}`);
        const permisosStr = res.data?.[0]?.PERMISOS ?? '';
        const arr = permisosStr ? permisosStr.split(',').map(Number).filter(Boolean) : [];
        setPermissions(arr);
      } catch (err) {
        console.error('Error al cargar los permisos:', err);
        setSnackbarMessage('Hubo un error al cargar los permisos.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [open, userId]);

  // Mantener "Marcar todo" sincronizado
  useEffect(() => {
    setSelectAll(permissions.length > 0 && permissions.length === Items.length);
  }, [permissions]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return Items;
    return Items.filter(it =>
      (it.title || '').toLowerCase().includes(q) ||
      String(it.id).includes(q)
    );
  }, [query]);

  const total = Items.length;
  const totalSelected = permissions.length;

  // Toggle individual
  const handleCheckboxChange = (itemId) => {
    setPermissions((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  // Marcar todo global
  const handleSelectAllChange = () => {
    if (selectAll) {
      setPermissions([]);
    } else {
      setPermissions(Items.map((it) => it.id));
    }
    setSelectAll(!selectAll);
  };

  // Marcar/limpiar SOLO lo visible (según filtro)
  const selectVisible = () => {
    setPermissions((prev) => {
      const set = new Set(prev);
      filteredItems.forEach((it) => set.add(it.id));
      return Array.from(set);
    });
  };

  const clearVisible = () => {
    setPermissions((prev) => prev.filter((id) => !filteredItems.some((fi) => fi.id === id)));
  };

  // Guardar
  const handleSavePermissions = async () => {
    setLoading(true);
    try {
      if (permissions.length === 0) {
        setSnackbarMessage('Debe seleccionar al menos un permiso.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }

      const permisosCadena = permissions.join(',');
      const permisosConTitulos = permissions.map((id) => ({
        id,
        title: Items.find((item) => item.id === id)?.title || `ID: ${id}`,
      }));

      const permisosEliminadoss = Items
        .filter((item) => !permissions.includes(item.id))
        .map((item) => ({ id: item.id, title: item.title }));

      await axios.post(`${config.apiBaseUrl}/SavePermisos/${userId}`, {
        permisos: permisosCadena,
        permisosConTitulos,
        permisosEliminadoss,
        usuarioModificador: loggedInUsername,
      });

      if (loggedInUsername === userName) {
        sessionStorage.setItem('Permisos', JSON.stringify(permissions));
      }

      setSnackbarMessage('Permisos guardados correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      window.dispatchEvent(new Event('permisosCambiados'));

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error al guardar permisos:', error);
      setSnackbarMessage('Hubo un error al guardar los permisos. Intente nuevamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        disableRestoreFocus
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid rgba(226,232,240,0.8)'
          }
        }}
      >
        {/* Header azul consistente */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: 'white',
              backdropFilter: 'blur(4px)',
              borderRadius: '12px'
            }}
          >
            <AdminPanelSettingsRoundedIcon fontSize="small" />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.2 }} noWrap>
              Asignación de Permisos de Módulo
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.88, fontSize: '0.78rem' }}>
              {userName || 'Usuario'} • {totalSelected} de {total} permisos asignados
            </Typography>
          </Box>

          <Tooltip title="Cerrar">
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {/* Toolbar de búsqueda y selección rápida */}
          <Paper
            elevation={0}
            sx={{
              p: 1.75,
              mb: 2,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Buscar módulo o vista por nombre..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: '1.1rem', color: '#64748b' }} />
                    </InputAdornment>
                  )
                }}
                sx={elegantInputSx}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    indeterminate={totalSelected > 0 && totalSelected < total}
                    onChange={handleSelectAllChange}
                    sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#0655A5' } }}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>Marcar todo</Typography>}
                sx={{ m: 0 }}
              />
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                {totalSelected} seleccionados de {total}
                {query ? ` • Filtro: ${filteredItems.length} coincidencia(s)` : ''}
              </Typography>

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={clearVisible}
                  sx={{
                    fontSize: '0.73rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#64748b',
                    py: 0.3,
                    px: 1.2
                  }}
                >
                  Limpiar visibles
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={selectVisible}
                  sx={{
                    fontSize: '0.73rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: '8px',
                    borderColor: '#cbd5e1',
                    color: '#0655A5',
                    py: 0.3,
                    px: 1.2
                  }}
                >
                  Seleccionar visibles ({filteredItems.length})
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Grid de tarjetas de permisos */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.25,
              maxHeight: 360,
              overflowY: 'auto',
              pr: 0.5
            }}
          >
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => {
                const checked = permissions.includes(item.id);
                return (
                  <Paper
                    key={item.id}
                    elevation={0}
                    onClick={() => handleCheckboxChange(item.id)}
                    sx={{
                      p: 1.25,
                      borderRadius: '10px',
                      border: checked ? '1px solid rgba(6,85,165,0.25)' : '1px solid #e2e8f0',
                      bgcolor: checked ? 'rgba(6,85,165,0.04)' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        bgcolor: checked ? 'rgba(6,85,165,0.08)' : '#f1f5f9',
                        borderColor: checked ? '#0655A5' : '#cbd5e1'
                      }
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      size="small"
                      tabIndex={-1}
                      disableRipple
                      sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: '#0655A5' } }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: checked ? 700 : 500,
                        color: checked ? '#0655A5' : '#1e293b',
                        lineHeight: 1.25,
                        userSelect: 'none'
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Paper>
                );
              })
            ) : (
              <Box sx={{ gridColumn: '1 / -1', py: 5, textAlign: 'center', color: '#64748b' }}>
                <Typography variant="body2" fontSize="0.85rem">
                  No hay permisos que coincidan con “<strong>{query}</strong>”.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.75, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              borderRadius: '10px',
              px: 2.5,
              py: 0.8,
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'none',
              color: '#64748b',
              borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePermissions}
            disabled={loading}
            sx={{
              borderRadius: '10px',
              px: 3,
              py: 0.8,
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 4px 12px rgba(6,85,165,0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #044384 0%, #0655A5 100%)',
                boxShadow: '0 6px 16px rgba(6,85,165,0.35)'
              }
            }}
          >
            Guardar Permisos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spinner (carga/guardado) */}
      <SimpleSuccessSpinner
        open={loading || fetching}
        handleClose={() => {}}
        message={fetching ? 'Cargando permisos...' : 'Guardando permisos...'}
      />

      {/* Snackbar */}
      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default ModalPermisos;